import { useCallback, useState, useMemo } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import { Box, Typography, visuallyHidden } from 'bonfire-kindling';
import { Redirect } from 'react-router-dom';
import { withRouter } from 'react-router';
import { useTranslation } from 'react-i18next';

import { useQueryParams, useDebounce } from 'helpers/hooks';
import { useVendor } from 'helpers/vendor';
import {
  useOpportunitiesActiveTab,
  useOpportunityTypes,
  useInfiniteOpportunitiesSearch,
  useInfiniteOpportunitiesSavedSearchProfile,
  OPPORTUNITIES_SEARCH_QUERY_KEYS,
} from 'helpers/opportunities';
import { regionDataToLocations } from 'helpers/locations';
import {
  SORT_BY,
  PROJECT_STATUS,
  PROJECT_SOURCE,
  PROJECT_VIEW_STATUS,
} from 'constants/opportunities';
import { allLocations } from 'constants/regions';
import { useIframeLogin } from '../hooks/useIframeLogin';
import OpportunitiesTabs from './_Tabs/OpportunitiesTabs';
import OpportunitiesList from './_List/OpportunitiesList';
import OpportunitiesSearchBelt from './_Search/OpportunitiesSearchBelt';

const Opportunities = ({ match, location }) => {
  const { t } = useTranslation();
  const query = useQueryParams();
  const activeTab = useOpportunitiesActiveTab();
  const { logInToOrganizationDomain, handleLoadGoToProjectIframe, iframeRef, iframeSrc } =
    useIframeLogin();

  const vendorQuery = useVendor();
  const opportunityTypesQuery = useOpportunityTypes({
    onSuccess: types => {
      setCurrentSearchFilter(prev => ({ ...prev, types }));
    },
  });

  const [currentSearchFilter, setCurrentSearchFilter] = useState({
    search: query.get('search') || '',
    sortBy: SORT_BY.mostRecent,
    locations: allLocations,
    statuses: PROJECT_STATUS.open,
    sources: PROJECT_SOURCE.allSources,
    viewStatus: PROJECT_VIEW_STATUS.allStatuses,
    types: opportunityTypesQuery.data ?? [],
    organizations: [],
    limit: 10,
  });

  const debouncedCurrentSearchFilter = useDebounce(currentSearchFilter, 500);

  // Avoids possible query deadlocks on the backend due to vendors immediately navigating
  // to "Opportunities" from the dashboard if the recommendation cache generation is
  // currently in process.
  const isFetchingDashboardRecommendedOpportunities = useIsFetching(
    OPPORTUNITIES_SEARCH_QUERY_KEYS.dashboard('recommended'),
    { exact: false }
  );

  const enabled =
    !!opportunityTypesQuery.data?.length &&
    !!vendorQuery.data &&
    !!debouncedCurrentSearchFilter.types.length;

  const recommended = useInfiniteOpportunitiesSearch('recommended', debouncedCurrentSearchFilter, {
    enabled: !isFetchingDashboardRecommendedOpportunities && enabled,
  });
  const following = useInfiniteOpportunitiesSearch('following', debouncedCurrentSearchFilter, {
    enabled,
  });
  const all = useInfiniteOpportunitiesSearch('all', debouncedCurrentSearchFilter, { enabled });
  const searchProfile = useInfiniteOpportunitiesSavedSearchProfile();

  const onFilterChange = useCallback(filters => {
    if (filters.locations === null) {
      filters.locations = [...allLocations];
    }
    if (filters.statuses === PROJECT_STATUS.closed) {
      // Since we remove the "Closing Soon" option when filtering by closed
      // projects, the sort column also needs to be updated.
      filters.sortBy = SORT_BY.closingSoon;
    }

    setCurrentSearchFilter(prev => ({ ...prev, ...filters }));
  }, []);

  const onSortChange = useCallback(e => {
    setCurrentSearchFilter(prev => ({ ...prev, sortBy: e.target.value }));
  }, []);

  const onSearchChange = useCallback(
    search => {
      onFilterChange({ search });
    },
    [onFilterChange]
  );

  const vendorLocations = useMemo(
    () => regionDataToLocations(vendorQuery.data?.regionData ?? null),
    [vendorQuery.data?.regionData]
  );

  const vendorLocationsSet = useMemo(() => new Set(vendorLocations), [vendorLocations]);

  const [availableLocations, effectiveLocations] = useMemo(() => {
    const isValidVendorLocation = location => vendorLocationsSet.has(location);

    // If the user navigates to either the 'Following' or 'All' tabs and selects
    // locations outside of their vendor locations and then navigates
    // back to 'Recommended', this causes those locations to still appear in
    // the filter when they shouldn't. In that case, just reset the filter
    // back to the vendor locations and preserve any valid selections.
    if (activeTab === 'recommended') {
      return [
        allLocations.filter(isValidVendorLocation),
        currentSearchFilter.locations.filter(isValidVendorLocation),
      ];
    }

    return [allLocations, currentSearchFilter.locations];
  }, [activeTab, currentSearchFilter.locations, vendorLocationsSet]);

  let opportunitiesQuery = null;
  switch (activeTab) {
    case 'recommended':
      opportunitiesQuery = recommended;
      break;
    case 'following':
      opportunitiesQuery = following;
      break;
    case 'all':
      opportunitiesQuery = all;
      break;
    case 'saved':
      opportunitiesQuery = searchProfile;
      break;
    default:
      break;
  }

  if (match.isExact || !opportunitiesQuery) {
    return <Redirect to={{ ...location, pathname: '/opportunities/recommended' }} />;
  }

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        sx={{ minHeight: '100%', bgcolor: 'background.main', width: '100%' }}
      >
        <Typography component="h1" style={visuallyHidden}>
          {t('opportunities.screen_reader.opportunities')}
        </Typography>
        <Box display="flex" mt={4} mb={2} mx={2}>
          <OpportunitiesTabs
            activeTab={activeTab}
            totalRecommended={recommended.total}
            totalFollowing={following.total}
            totalAll={all.total}
            totalSaved={searchProfile.total}
          />
        </Box>
        <Box m={2}>
          <OpportunitiesSearchBelt
            availableTypes={opportunityTypesQuery.data ?? []}
            availableLocations={availableLocations}
            effectiveLocations={effectiveLocations}
            currentSearchFilter={currentSearchFilter}
            onFilterChange={onFilterChange}
            onSearchChange={onSearchChange}
            onSortChange={onSortChange}
          />
        </Box>
        <OpportunitiesList
          activeTab={activeTab}
          opportunitiesQuery={opportunitiesQuery}
          logInToOrganizationDomain={logInToOrganizationDomain}
          currentSearchFilter={currentSearchFilter}
        />
      </Box>
      {iframeSrc && (
        <iframe
          style={{ display: 'none' }}
          ref={iframeRef}
          title={t('opportunities.project.go_to_project')}
          src={iframeSrc}
          onLoad={handleLoadGoToProjectIframe}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default withRouter(Opportunities);
